import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MicrosoftOutlookLogo } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { Panel, Badge, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const start = async (body) => {
  // Microsoft's sign-in page refuses to load inside frames (e.g. the preview pane), so open it top-level in a new tab.
  // The tab is opened synchronously (inside the click) so popup blockers allow it, then pointed at Microsoft.
  const w = window.open("about:blank", "_blank");
  const r = await act(() => api.post("/oauth/microsoft/start", body));
  if (!r?.url) { w?.close(); return; }
  if (w) {
    w.opener = null;
    w.location.href = r.url;
    toast.info("Microsoft sign-in opened in a new tab. Come back here when it says connected.");
  } else {
    try { window.top.location.href = r.url; } catch { window.location.href = r.url; }
  }
};

export const Microsoft365Panel = ({ reps }) => {
  const { data, reload } = useFetch("/oauth/microsoft/status");
  const [mb, setMb] = useState("");
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const msg = params.get("ms");
    if (!msg) return;
    msg.startsWith("error") ? toast.error("Microsoft 365 sign-in failed", { description: msg.replace(/^error:\s*/, "") }) : toast.success(`Microsoft 365 ${msg}`);
    params.delete("ms");
    setParams(params, { replace: true });
  }, []); // eslint-disable-line
  useEffect(() => {
    window.addEventListener("focus", reload);
    return () => window.removeEventListener("focus", reload);
  }, [reload]);
  if (!data) return null;
  const mailbox = data.connections.find((c) => c.kind === "mailbox");
  const cal = (email) => data.connections.find((c) => c.kind === "calendar" && c.rep_email === email.toLowerCase());
  return (
    <Panel title="Microsoft 365 · shared mailbox & calendars" className="lg:col-span-2" data-testid="ms365-panel">
      {!data.app_ready && (
        <div className="text-xs text-[#FFB020] border border-[#FFB020]/30 rounded-md p-3 mb-4" data-testid="ms365-app-missing">
          One-time IT step: register a Microsoft app with <b>delegated</b> permissions only (offline_access, User.Read, Mail.ReadWrite.Shared, Mail.Send.Shared, Calendars.ReadWrite) and redirect URI
          <code className="block mt-1 text-zinc-300 break-all">{data.redirect_uri}</code>
          Delegated access means the platform can reach only the mailbox/calendar of whoever signs in, never the whole organisation.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Shared mailbox</div>
          {mailbox ? (
            <div className="flex items-center gap-3 text-sm" data-testid="ms365-mailbox-connected">
              <MicrosoftOutlookLogo size={22} className="text-[#3395FF]" />
              <div className="flex-1"><div>{mailbox.shared_mailbox}</div><div className="text-[11px] text-zinc-500">via {mailbox.ms_user} · {fmtDate(mailbox.created_at)}</div></div>
              <Badge v={mailbox.status} />
              <Button size="sm" variant="ghost" data-testid="ms365-mailbox-disconnect" onClick={async () => { await act(() => api.delete(`/oauth/microsoft/${mailbox.id}`), "Disconnected"); reload(); }}>Disconnect</Button>
            </div>
          ) : null}
          <Field label="Shared mailbox address" hint="Sign in as a user who has Full Access + Send As on this mailbox. Only this mailbox is used.">
            <Input data-testid="ms365-mailbox-input" value={mb} onChange={(e) => setMb(e.target.value)} placeholder="sales@specia.ai" className="bg-black" />
          </Field>
          <Button data-testid="ms365-mailbox-connect" disabled={!data.app_ready || !mb.includes("@")} onClick={() => start({ kind: "mailbox", shared_mailbox: mb })} className="bg-[#007AFF] hover:bg-[#3395FF]">{mailbox ? "Reconnect" : "Sign in with that mailbox"}</Button>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Rep calendars (each rep signs in to their own)</div>
          {(reps || []).map((r) => {
            const c = cal(r.email);
            return (
              <div key={r.email} className="flex items-center gap-3 text-sm py-1" data-testid={`ms365-rep-${r.email}`}>
                <div className="flex-1"><div>{r.name}</div><div className="text-[11px] text-zinc-500 font-mono">{r.email}</div></div>
                <Badge v={c ? c.status : "not_connected"} />
                <Button size="sm" variant="secondary" disabled={!data.app_ready} data-testid={`ms365-rep-connect-${r.email}`} onClick={() => start({ kind: "calendar", rep_email: r.email })}>{c ? "Reconnect" : "Connect calendar"}</Button>
              </div>
            );
          })}
          <p className="text-[11px] text-zinc-500">Booking rechecks the rep's free/busy and creates one Teams event with the prospect and rep as attendees.</p>
        </div>
      </div>
    </Panel>
  );
};
